import React, { useState, useRef, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../firebase';

const EditRequestModal = ({ 
  showEditRequestModal, 
  setShowEditRequestModal, 
  selectedRequest, 
  handleEditRequest, 
  saving 
}) => {
  const [editRequest, setEditRequest] = useState({
    title: '',
    description: '',
    budget: '',
    location: '',
    category: 'solar-panels',
    images: []
  });
  const [customCategory, setCustomCategory] = useState('');
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [newImages, setNewImages] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef(null);

  // Load request data when modal opens
  useEffect(() => {
    if (selectedRequest && showEditRequestModal) {
      setEditRequest({
        title: selectedRequest.title || '',
        description: selectedRequest.description || '',
        budget: selectedRequest.budget || '',
        location: selectedRequest.location || '',
        category: selectedRequest.category || 'solar-panels',
        images: selectedRequest.images || []
      });
      setCustomCategory(selectedRequest.category && !['solar-panels', 'inverters', 'batteries', 'charge-controllers', 'solar-freezers', 'street-lights', 'installation', 'maintenance'].includes(selectedRequest.category) ? selectedRequest.category : '');
      setShowCustomCategory(selectedRequest.category && !['solar-panels', 'inverters', 'batteries', 'charge-controllers', 'solar-freezers', 'street-lights', 'installation', 'maintenance'].includes(selectedRequest.category));
    }
  }, [selectedRequest, showEditRequestModal]);

  const handleImageUpload = async (files) => {
    setUploadingImages(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const storageRef = ref(storage, `requests/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        return {
          url: downloadURL,
          name: file.name,
          ref: snapshot.ref
        };
      });

      const uploadedImages = await Promise.all(uploadPromises);
      setNewImages(prev => [...prev, ...uploadedImages]);
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Error uploading images. Please try again.');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteNewImage = async (imageToDelete) => {
    try {
      await deleteObject(imageToDelete.ref);
      setNewImages(prev => prev.filter(img => img.url !== imageToDelete.url));
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Error deleting image. Please try again.');
    }
  };

  const handleDeleteExistingImage = (imageUrl) => {
    setEditRequest(prev => ({
      ...prev,
      images: prev.images.filter(img => img !== imageUrl)
    }));
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleImageUpload(files);
    }
  };

  const handleSaveEdit = async () => {
    // Combine existing and new images
    const allImages = [...editRequest.images, ...newImages.map(img => img.url)];
    
    const updatedRequest = {
      ...editRequest,
      images: allImages
    };
    
    await handleEditRequest(selectedRequest.id, updatedRequest);
    
    // Clear new images after successful submission
    setNewImages([]);
  };

  if (!showEditRequestModal || !selectedRequest) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Edit Request</h3>
          <div className="flex gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition"
            >
              {showPreview ? 'Edit' : 'Preview'}
            </button>
            <button
              onClick={() => setShowEditRequestModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {showPreview ? (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-lg mb-2">{editRequest.title}</h4>
              <p className="text-gray-600 mb-3">{editRequest.description}</p>
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                <span>📍 {editRequest.location}</span>
                <span>💰 {editRequest.budget}</span>
                <span>📦 {editRequest.category}</span>
              </div>
              {editRequest.images.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {editRequest.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={editRequest.title}
                onChange={(e) => setEditRequest(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Need 450W Solar Panels"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-abiu-green"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                rows="3"
                value={editRequest.description}
                onChange={(e) => setEditRequest(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what you need..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-abiu-green"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Budget *</label>
                <input
                  type="text"
                  value={editRequest.budget}
                  onChange={(e) => setEditRequest(prev => ({ ...prev, budget: e.target.value }))}
                  placeholder="₦50,000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-abiu-green"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                <input
                  type="text"
                  value={editRequest.location}
                  onChange={(e) => setEditRequest(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Lagos"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-abiu-green"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={editRequest.category}
                onChange={(e) => {
                  const value = e.target.value;
                  setEditRequest(prev => ({ ...prev, category: value }));
                  setShowCustomCategory(value === 'custom');
                  if (value !== 'custom') {
                    setCustomCategory('');
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-abiu-green"
              >
                <option value="solar-panels">Solar Panels</option>
                <option value="inverters">Inverters</option>
                <option value="batteries">Batteries</option>
                <option value="charge-controllers">Charge Controllers</option>
                <option value="solar-freezers">Solar Freezers</option>
                <option value="street-lights">Street Lights</option>
                <option value="installation">Installation Services</option>
                <option value="maintenance">Maintenance Services</option>
                <option value="custom">Custom Category</option>
              </select>
            </div>
            
            {showCustomCategory && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Category</label>
                <input
                  type="text"
                  value={customCategory}
                  onChange={(e) => {
                    setCustomCategory(e.target.value);
                    setEditRequest(prev => ({ ...prev, category: e.target.value }));
                  }}
                  placeholder="Enter your custom category"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-abiu-green"
                />
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Images</label>
              
              {/* Existing Images */}
              {editRequest.images.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-2">Current Images:</p>
                  <div className="grid grid-cols-3 gap-2">
                    {editRequest.images.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image}
                          alt={`Current ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteExistingImage(image)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete image"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Add New Images */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImages || (editRequest.images.length + newImages.length) >= 3}
                className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-abiu-green focus:outline-none focus:ring-2 focus:ring-abiu-green transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingImages ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-abiu-green border-t-transparent rounded-full animate-spin mr-2"></div>
                    Uploading...
                  </div>
                ) : (
                  <div className="text-center">
                    <svg className="w-6 h-6 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-sm text-gray-600">
                      Add more images ({(editRequest.images.length + newImages.length)}/3)
                    </span>
                  </div>
                )}
              </button>
              
              {/* New Images */}
              {newImages.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">New Images:</p>
                  <div className="grid grid-cols-3 gap-2">
                    {newImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image.url}
                          alt={`New ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteNewImage(image)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete image"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSaveEdit}
            disabled={saving || !editRequest.title || !editRequest.description || !editRequest.budget || !editRequest.location}
            className="flex-1 bg-abiu-green text-white py-2 rounded-lg font-semibold hover:bg-abiu-green-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            onClick={() => setShowEditRequestModal(false)}
            className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-300 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditRequestModal; 